import { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Loader2, RotateCcw, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { useAuthStore } from '@/lib/auth/store';
import { BASE_PERMISSION } from '@/lib/constants/modules';
import { usePermission } from '@/lib/permissions/use-permission';
import { formatDate } from '@/lib/utils/date';
import { useAdminHistoricalCampaigns, useCampaignReportDownload } from '../api';
import { buildCampaignColumns, DEFAULT_VISIBLE_COLUMNS } from '../columns';
import { BaseNumbersDialog } from '../components/BaseNumbersDialog';
import type { CampaignRow } from '../types';

type FilterChoice = 'none' | 'username' | 'campaign_id';
const FILTER_OPTIONS: Array<{ value: FilterChoice; label: string; numeric?: boolean }> = [
  { value: 'none', label: 'No filter' },
  { value: 'username', label: 'Username' },
  { value: 'campaign_id', label: 'Campaign ID', numeric: true },
];

export function AdminHistoricalCampaignsPage() {
  const userId = useAuthStore((s) => s.userId);
  // SADMIN / sub-super-admin see the Parent Username column; resellers don't.
  const showParentUsername = useAuthStore((s) => s.isSubOrSuperAdmin());
  const search = useAdminHistoricalCampaigns();
  const rows = search.data ?? [];
  const [baseCampaignId, setBaseCampaignId] = useState<number | null>(null);

  const [startDate, setStartDate] = useState<Date | undefined>(() => new Date());
  const [endDate, setEndDate] = useState<Date | undefined>(() => new Date());
  const [filterChoice, setFilterChoice] = useState<FilterChoice>('none');
  const [filterDraft, setFilterDraft] = useState('');
  const [hasSearched, setHasSearched] = useState(false);

  const numericFilter = FILTER_OPTIONS.find((o) => o.value === filterChoice)?.numeric ?? false;

  // Auto-load last 7 days on mount
  useEffect(() => {
    if (!userId) return;

    const today = new Date();
    const start = today;
    const end = today;

    search.mutate({
  userId: Number(userId),
  startDate: formatDate(start),
  endDate: formatDate(end),
  filterBy: '',
  filterData: '',
  pageSize: 5000,   // ← add
  pageNo: 1,    
});

    setHasSearched(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const handleViewBase = (row: CampaignRow) => setBaseCampaignId(Number(row.campaignId));

  const reportDownload = useCampaignReportDownload();
  const columns = useMemo(
    () =>
      buildCampaignColumns({
        showOwner: true,
        showParentUsername,
        onViewBase: showParentUsername ? handleViewBase : undefined,
        onDownloadReport: reportDownload.download,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [showParentUsername, reportDownload.download],
  );

  const visibility = {
    ...DEFAULT_VISIBLE_COLUMNS,
    userId: true,
    username: true,
    parentUsername: showParentUsername,
    accountType: showParentUsername,
  };

  const canView = usePermission('Manage Campaign.Historical Campaign Summary', BASE_PERMISSION.VIEW);

  if (!canView) return <Navigate to="/" replace />;

  const handleSearch = () => {
    if (startDate && endDate && startDate > endDate) {
      toast.error('Start date cannot be after end date.');
      return;
    }
    if (filterChoice !== 'none' && !filterDraft.trim()) {
      toast.error('Please enter a filter value.');
      return;
    }
    setHasSearched(true);
    const filterBy = filterChoice === 'none' ? '' : filterChoice;
const filterData = filterDraft.trim();
    search.mutate(
      {
        userId: Number(userId),
        startDate: startDate ? formatDate(startDate) : '',
        endDate: endDate ? formatDate(endDate) : '',
        filterBy,
        filterData,
        pageSize: 5000,   // ← add
        pageNo: 1,   
      },
      {
        onError: () => toast.error('Could not fetch historical campaigns.'),
      },
    );
  };

    const handleReset = () => {
    const today = new Date();
    setStartDate(today);
    setEndDate(today);
    setFilterChoice('none');
    setFilterDraft('');
    setHasSearched(true);
    search.mutate(
      {
        userId: Number(userId),
        startDate: formatDate(today),
        endDate: formatDate(today),
        filterBy: '',
        filterData: '',
        pageSize: 5000,
        pageNo: 1,
      },
      {
        onError: () => toast.error('Could not fetch historical campaigns.'),
      },
    );
  };

  return (
    <FillPage>
      <PageHeader
        title="Campaign History"
        description="Look up completed campaigns across users by date and optional username."
      />

      <Card>
        <CardHeader>
          <CardTitle>Filter</CardTitle>
          <CardDescription>
            Combine date range with an optional field-specific filter.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_1fr_1fr_1fr_auto_auto] sm:items-end">
            <div className="space-y-1.5">
              <Label htmlFor="start-date">Start date</Label>
              <DatePicker value={startDate} onChange={setStartDate} placeholder="Start date" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="end-date">End date</Label>
              <DatePicker value={endDate} onChange={setEndDate} placeholder="End date" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="filter-by">Field</Label>
              <Select
                value={filterChoice}
                onValueChange={(v) => {
                  setFilterChoice(v as FilterChoice);
                  setFilterDraft('');
                }}
              >
                <SelectTrigger id="filter-by">
                  <SelectValue placeholder="No filter" />
                </SelectTrigger>
                <SelectContent>
                  {FILTER_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="filter-data">Value</Label>
              <Input
                id="filter-data"
                value={filterDraft}
                inputMode={numericFilter ? 'numeric' : 'text'}
                onChange={(e) => {
                  const v = e.target.value;
                  setFilterDraft(numericFilter ? v.replace(/[^\d]/g, '') : v);
                }}
                placeholder="Optional"
                disabled={filterChoice === 'none'}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
            </div>
            <Button onClick={handleSearch} disabled={search.isPending}>
              {search.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Search className="h-4 w-4" />
              )}
              Search
            </Button>
             {/* ← ye add karo Search button ke neeche */}
            <Button variant="outline" onClick={handleReset} disabled={search.isPending}>
              <RotateCcw className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      <DataTable<CampaignRow, unknown>
        tableId="campaigns:voice:admin-historical:v1"
        columns={columns}
        data={rows}
        isLoading={search.isPending}
        searchPlaceholder="Filter results…"
        emptyTitle={hasSearched ? 'No campaigns match' : 'Pick a range to begin'}
        emptyDescription={
          hasSearched
            ? 'Try widening the date range or removing the username filter.'
            : 'Choose start / end dates above and click Search.'
        }
        getRowId={(row) => String(row.campaignId)}
        initialColumnVisibility={visibility}
        exportFilename="voice-campaigns-admin-historical.csv"
        pageSize={10}
      />

      <BaseNumbersDialog campaignId={baseCampaignId} onClose={() => setBaseCampaignId(null)} />
    </FillPage>
  );
}
